import React, { useEffect, useLayoutEffect, useState } from "react";
import {
  Box,
  Button,
  CloseButton,
  Flex,
  Spinner,
  Text,
  toastStore,
} from "@chakra-ui/react";
import { tt } from "../i18n/tt";

/**
 * 앱 공용 토스트 (2026-09-06 시안 C「상단 컴팩트 스트립」 — docs/design/admin-toast-variants.html)
 *
 * Chakra `useToast()` 호출부(120여 파일)는 그대로 두고, `ChakraProvider toastOptions` 의
 * `defaultOptions.render` 로 이 컴포넌트를 꽂는다. 상태 색은 아이콘·테두리·배경 워시에만 쓰고
 * 글자는 항상 잉크색(neutral.800)이다 — Chakra 기본 solid(채도 높은 면 + 흰 글자)는 4종 모두
 * 본문 대비 AA 미달이라 폐기.
 *
 * 추가로 받는 옵션(선택):
 *   - `action: { label, onClick }` — 오른쪽 끝 칩 버튼 1개. 누르면 onClick 후 닫힌다.
 *
 * 중복 방지: 같은 status+title+description 이 이미 떠 있으면 새 토스트를 띄우지 않고
 * 기존 토스트의 카운트(×2)를 올리며 타이머를 다시 잰다. 호출부에 `id` 를 넣지 않아도 된다.
 */

const TONE = {
  error: { c: "status.error", wash: "statusWash.error", line: "statusLine.error", role: "alert" },
  warning: { c: "status.warning", wash: "statusWash.warning", line: "statusLine.warning", role: "alert" },
  success: { c: "status.success", wash: "statusWash.success", line: "statusLine.success", role: "status" },
  info: { c: "status.info", wash: "statusWash.info", line: "statusLine.info", role: "status" },
  loading: { c: "neutral.600", wash: "neutral.100", line: "neutral.300", role: "status" },
};

const svgProps = {
  viewBox: "0 0 16 16",
  width: 12,
  height: 12,
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2.4,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
};

// 색약 사용자를 위해 상태마다 아이콘 형태를 다르게 둔다 (✕ / ! / ✓ / i).
const ICON = {
  error: (
    <svg {...svgProps}>
      <path d="M5 5l6 6M11 5l-6 6" />
    </svg>
  ),
  warning: (
    <svg {...svgProps}>
      <path d="M8 4.5v4M8 11.5h.01" />
    </svg>
  ),
  success: (
    <svg {...svgProps}>
      <path d="M3.5 8.5l3 3 6-6" />
    </svg>
  ),
  info: (
    <svg {...svgProps}>
      <path d="M8 7.5v4M8 4.5h.01" />
    </svg>
  ),
  loading: <Spinner size="xs" thickness="2px" speed="0.7s" />,
};

const isPlain = (v) => v == null || typeof v === "string" || typeof v === "number";

// 현재 떠 있는 토스트: 중복 키 → { id, options }
const live = new Map();

const dedupeKey = ({ status, title, description }) =>
  isPlain(title) && isPlain(description) ? `${status}|${title}|${description}` : null;

export default function AppToast(props) {
  const { onClose, ...options } = props;
  const { id, status = "info", title, description, isClosable, action, count = 1 } = options;
  const tone = TONE[status] || TONE.info;
  const key = dedupeKey(options);
  const [duplicate, setDuplicate] = useState(false);

  useLayoutEffect(() => {
    if (!key) return undefined;
    const existing = live.get(key);
    if (existing && existing.id !== id && toastStore.isActive(existing.id)) {
      const base = existing.options.baseDuration ?? existing.options.duration;
      // duration 값이 바뀌어야 ToastComponent 의 타이머가 다시 시작된다 — 1ms 를 번갈아 더한다.
      const nextDuration = base == null ? base : existing.options.duration === base ? base + 1 : base;
      toastStore.update(existing.id, {
        ...existing.options,
        baseDuration: base,
        duration: nextDuration,
        count: (existing.options.count || 1) + 1,
      });
      setDuplicate(true);
      onClose();
      return undefined;
    }
    live.set(key, { id, options });
    return () => {
      if (live.get(key)?.id === id) live.delete(key);
    };
    // 마운트 시 1회 판정. key/id 는 토스트 수명 동안 바뀌지 않는다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // update 로 count 가 오른 뒤에도 다음 중복이 최신 옵션을 이어받도록 갱신
  useEffect(() => {
    if (!key) return;
    const entry = live.get(key);
    if (entry && entry.id === id) entry.options = options;
  });

  if (duplicate) return null;

  return (
    <Flex
      role={tone.role}
      align="center"
      gap="10px"
      bg={tone.wash}
      color="neutral.800"
      border="1px solid"
      borderColor={tone.line}
      borderRadius="999px"
      pl="10px"
      pr={isClosable ? "6px" : "14px"}
      py="7px"
      maxW="min(560px, calc(100vw - 24px))"
      fontSize="13px"
      lineHeight="1.4"
      boxShadow="0 2px 4px rgba(16,32,32,.06), 0 12px 32px rgba(16,32,32,.14)"
      data-status={status}
    >
      <Flex
        flex="none"
        w="20px"
        h="20px"
        borderRadius="full"
        bg={tone.c}
        color="white"
        align="center"
        justify="center"
      >
        {ICON[status] || ICON.info}
      </Flex>

      <Box flex="1 1 auto" minW={0} noOfLines={2} wordBreak="keep-all">
        <Text as="span" fontWeight="700">
          {title}
        </Text>
        {description != null && description !== "" && (
          <Text as="span" color="neutral.600">
            <Text as="span" color="neutral.400" mx="7px" aria-hidden="true">
              ·
            </Text>
            {description}
          </Text>
        )}
      </Box>

      {count > 1 && (
        <Box
          flex="none"
          fontSize="11px"
          fontWeight="700"
          fontFamily="mono"
          color="white"
          bg={tone.c}
          borderRadius="999px"
          px="6px"
          lineHeight="16px"
          aria-label={tt("a.fc809d11", "{{v0}}회 반복", { v0: count })}
        >
          ×{count}
        </Box>
      )}

      {action?.label && (
        <Button
          size="xs"
          variant="outline"
          borderRadius="999px"
          bg="white"
          color={tone.c}
          borderColor={tone.line}
          fontWeight="700"
          flex="none"
          _hover={{ bg: tone.wash }}
          onClick={() => {
            action.onClick?.();
            onClose();
          }}
        >
          {action.label}
        </Button>
      )}

      {isClosable && (
        <CloseButton
          size="sm"
          borderRadius="full"
          color="neutral.500"
          flex="none"
          aria-label={tt("a.be8bbfb2", "알림 닫기")}
          onClick={onClose}
        />
      )}
    </Flex>
  );
}

/** `<ChakraProvider toastOptions={appToastOptions}>` — App.jsx 와 데모 엔트리가 같이 쓴다. */
export const appToastOptions = {
  defaultOptions: {
    position: "top",
    duration: 4000,
    isClosable: true,
    // Chakra 래퍼의 minWidth 300 / margin 0.5rem 을 걷어내고 알약이 내용 폭만 차지하게 한다
    containerStyle: { minWidth: 0, maxWidth: "none", margin: "12px 12px 0" },
    render: (props) => <AppToast {...props} />,
  },
};
